import { ref, computed } from 'vue';

export function useDashboardUtils() {
  // Common formatting functions
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₱0.00';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Status and class helpers
  const getQuotationStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getOrderStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_review':
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'production':
        return 'bg-purple-100 text-purple-800';
      case 'quality_check':
        return 'bg-indigo-100 text-indigo-800';
      case 'ready_release':
        return 'bg-green-100 text-green-800';
      case 'released':
        return 'bg-teal-100 text-teal-800';
      case 'for_collection':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getPaymentMethodClass = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'bg-green-100 text-green-800';
      case 'bank_transfer':
        return 'bg-blue-100 text-blue-800';
      case 'check':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'fas fa-money-bill-wave';
      case 'bank_transfer':
        return 'fas fa-university';
      case 'check':
        return 'fas fa-money-check-alt';
      default:
        return 'fas fa-wallet';
    }
  };
  
  const formatPaymentMethod = (method) => {
    if (!method) return 'N/A';
    return method.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const getOutstandingClass = (amount) => {
    if (amount >= 100000) return 'text-red-600';
    if (amount >= 50000) return 'text-yellow-600';
    return 'text-green-600';
  };
  
  const getFollowUpStatus = (dateReceived) => {
    const days = Math.floor((new Date() - new Date(dateReceived)) / (1000 * 60 * 60 * 24));
    if (days > 30) return '30+ days overdue';
    if (days > 15) return '15+ days overdue';
    return `${days} days old`;
  };
  
  const getUrgencyClass = (days_old) => {
    if (days_old >= 30) return 'bg-red-50';
    if (days_old >= 15) return 'bg-yellow-50';
    return 'bg-blue-50';
  };
  
  const getStatusColor = (days_old) => {
    if (days_old >= 30) return 'text-red-600';
    if (days_old >= 15) return 'text-yellow-600';
    return 'text-blue-600';
  };
  
  // Text utilities
  const truncateText = (text, length = 15) => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };
  
  // Date utilities
  const getCurrentFormattedDate = () => {
    // Get the current date in local timezone
    const today = new Date();
    
    // Extract date components directly from the Date object
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    
    const formattedDate = `${year}-${month}-${day}`;
    console.log('[useDashboardUtils] Current formatted date:', formattedDate);
    return formattedDate;
  };
  
  return {
    formatCurrency,
    formatDate,
    getQuotationStatusClass,
    getOrderStatusClass,
    getPaymentMethodClass,
    getPaymentMethodIcon,
    formatPaymentMethod,
    getOutstandingClass,
    getFollowUpStatus,
    getUrgencyClass,
    getStatusColor,
    truncateText,
    getCurrentFormattedDate
  };
} 